import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function toIntOrNull(value) {
	if (value === '' || value === null || value === undefined) return null;
	const parsed = parseInt(value, 10);
	return Number.isNaN(parsed) ? null : parsed;
}

function computeAlphaSortValue(label) {
	if (!label) return null;
	const normalized = label.trim().toUpperCase();
	if (!normalized) return null;
	const tokens = normalized.replace(/[^A-Z0-9]/g, '');
	if (!tokens) return null;
	const max = Math.min(4, tokens.length);
	let value = 0;
	for (let i = 0; i < max; i += 1) {
		value = value * 100 + tokens.charCodeAt(i);
	}
	return value;
}

function buildModelLabel(row) {
	if (!row) return '';
	const parts = [row.Make, row.ModelName];
	if (row.StreetName) parts.push(row.StreetName);
	if (row.Year !== null && row.Year !== undefined) parts.push(row.Year);
	return parts.filter(Boolean).join(' ');
}

export default function AddOneOffModal({
	open,
	onClose,
	baseRow,
	productGroupKey,
	subProductGroupKey,
	modelKey,
	existingFitmentLocationKeys,
	onAddGhostRow,
	onOneOffPromoted,
	canCreateItemType
}) {
	const [options, setOptions] = useState([]);
	const [allLocations, setAllLocations] = useState([]);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [optionsError, setOptionsError] = useState('');
	const [selectedOption, setSelectedOption] = useState('');
	const [resolvedContext, setResolvedContext] = useState(null);

	const [newLocationName, setNewLocationName] = useState('');
	const [creatingLocation, setCreatingLocation] = useState(false);

	const [customItemTypeName, setCustomItemTypeName] = useState('');
	const [customLocationName, setCustomLocationName] = useState('');
	const [creatingItemType, setCreatingItemType] = useState(false);
	const [itemTypeError, setItemTypeError] = useState('');

	const modelLabel = useMemo(() => buildModelLabel(baseRow), [baseRow]);

	const effectiveProductGroupKey = useMemo(() => {
		const candidates = [
			productGroupKey,
			resolvedContext?.productGroupKey,
			baseRow?.ParentProductGroupKey,
			baseRow?.parentProductGroupKey,
			baseRow?.ProductGroupKey,
			baseRow?.productGroupKey
		];
		return candidates.find(value => value !== null && value !== undefined) ?? null;
	}, [productGroupKey, resolvedContext, baseRow]);

	const effectiveSubProductGroupKey = useMemo(() => {
		const candidates = [
			subProductGroupKey,
			resolvedContext?.subProductGroupKey,
			baseRow?.SubProductGroupKey,
			baseRow?.subProductGroupKey
		];
		return candidates.find(value => value !== null && value !== undefined) ?? null;
	}, [subProductGroupKey, resolvedContext, baseRow]);

	const filteredOptions = useMemo(() => {
		if (!open || !baseRow) return [];
		const map = new Map();
		const addOption = (opt, meta = {}) => {
			if (!opt || opt.FitmentLocationKey == null) return;
			const keyStr = String(opt.FitmentLocationKey);
			const existing = map.get(keyStr) || {};
			const merged = {
				FitmentLocationKey: existing.FitmentLocationKey ?? opt.FitmentLocationKey,
				FitmentLocation: opt.FitmentLocation ?? opt.location ?? opt.name ?? existing.FitmentLocation ?? '',
				SortOrder: opt.SortOrder ?? opt.sortOrder ?? existing.SortOrder ?? null,
				OneOffGroupKey: opt.OneOffGroupKey ?? opt.oneOffGroupKey ?? existing.OneOffGroupKey ?? null,
				isOneOff: meta.isOneOff ?? existing.isOneOff ?? false,
				isCustom: meta.isCustom ?? existing.isCustom ?? false
			};
			map.set(keyStr, merged);
		};

		if (baseRow?.FitmentLocationKey != null) {
			addOption({
				FitmentLocationKey: baseRow.FitmentLocationKey,
				FitmentLocation: baseRow.FitmentLocation ?? '',
				SortOrder: baseRow.LocSortOrder ?? baseRow.SortOrder ?? null,
				OneOffGroupKey: baseRow.OneOffGroupKey ?? null
			}, { isOneOff: !!baseRow?.OneOffGroupKey });
		}

		options.forEach(opt => addOption(opt, {
			isOneOff: opt.isOneOff ?? !!(opt.OneOffGroupKey ?? opt.oneOffGroupKey)
		}));
		allLocations.forEach(opt => addOption(opt, {
			isOneOff: opt.isOneOff ?? false,
			isCustom: opt.IsCustom ?? opt.isCustom ?? false
		}));

		const existingSet = new Set((existingFitmentLocationKeys || []).filter(key => key != null).map(key => String(key)));
		const list = Array.from(map.values()).map(opt => ({
			...opt,
			isCurrent: baseRow?.FitmentLocationKey != null && String(baseRow.FitmentLocationKey) === String(opt.FitmentLocationKey),
			isAlreadyOnModel: existingSet.has(String(opt.FitmentLocationKey))
		}));
		list.sort((a, b) => (a.FitmentLocation || '').localeCompare(b.FitmentLocation || '', undefined, { sensitivity: 'base' }));
		return list;
	}, [open, baseRow, options, allLocations, existingFitmentLocationKeys]);

	useEffect(() => {
		if (!open || !baseRow) return;
		let isCancelled = false;
		const loadOptions = async () => {
			setOptionsLoading(true);
			setOptionsError('');
			try {
				let fetched = [];
				let contextPayload = null;
				let fullLocations = [];
				if (!productGroupKey) {
					if (!modelKey) {
						throw new Error('Missing model context for one-off options.');
					}
					const [optionsRes, locationsRes] = await Promise.all([
						axios.get(`/api/models/${modelKey}/one-off-options`, {
							params: { itemTypeKey: baseRow.ItemTypeKey }
						}),
						axios.get(`/api/item-types/${baseRow.ItemTypeKey}/fitment-locations`)
					]);
					const data = optionsRes?.data;
					if (isCancelled) return;
					if (Array.isArray(data)) {
						fetched = data;
					} else {
						fetched = Array.isArray(data?.options) ? data.options : [];
						contextPayload = data?.context || null;
						// In the by-model view we allow contextless creation, so we treat
						// any server message here as informational rather than blocking.
					}
					fullLocations = Array.isArray(locationsRes?.data) ? locationsRes.data : [];
				} else {
					const modelKeyParam = modelKey != null ? `&modelKey=${encodeURIComponent(modelKey)}` : '';
					const url = `/api/one-off-options?pgKey=${encodeURIComponent(productGroupKey)}`
						+ (subProductGroupKey ? `&spgKey=${encodeURIComponent(subProductGroupKey)}` : '')
						+ `&itemTypeKey=${encodeURIComponent(baseRow.ItemTypeKey)}`
						+ modelKeyParam;
					const [optionsRes, locationsRes] = await Promise.all([
						axios.get(url),
						axios.get(`/api/item-types/${baseRow.ItemTypeKey}/fitment-locations`)
					]);
					const data = optionsRes?.data;
					if (isCancelled) return;
					fetched = Array.isArray(data) ? data : [];
					fullLocations = Array.isArray(locationsRes?.data) ? locationsRes.data : [];
				}
				setResolvedContext(contextPayload);
				setOptions(fetched.map(opt => ({
					FitmentLocationKey: opt.FitmentLocationKey ?? opt.fitmentLocationKey,
					FitmentLocation: opt.FitmentLocation ?? opt.location ?? opt.name ?? 'Unnamed',
					SortOrder: opt.SortOrder ?? opt.sortOrder ?? null,
					OneOffGroupKey: opt.OneOffGroupKey ?? opt.oneOffGroupKey ?? null,
					isOneOff: true
				})).filter(opt => opt.FitmentLocationKey != null));
				setAllLocations(fullLocations.map(row => ({
					FitmentLocationKey: row.FitmentLocationKey ?? row.fitmentLocationKey,
					FitmentLocation: row.FitmentLocation ?? row.Location ?? row.location ?? 'Unnamed',
					SortOrder: row.SortOrder ?? row.sortOrder ?? null,
					OneOffGroupKey: row.OneOffGroupKey ?? row.oneOffGroupKey ?? null,
					isOneOff: !!(row.OneOffGroupKey ?? row.oneOffGroupKey),
					isCustom: !!(row.IsCustom ?? row.isCustom)
				})).filter(opt => opt.FitmentLocationKey != null));
			} catch (err) {
				if (!isCancelled) {
					console.error('Failed to load fitment locations:', err);
					setOptions([]);
					setAllLocations([]);
					setOptionsError('Failed to load fitment locations. Try again or create a new one.');
				}
			} finally {
				if (!isCancelled) setOptionsLoading(false);
			}
		};
		loadOptions();
		return () => {
			isCancelled = true;
			setOptionsLoading(false);
		};
	}, [open, baseRow, productGroupKey, subProductGroupKey, modelKey]);

	useEffect(() => {
		if (!open) {
			setOptions([]);
			setAllLocations([]);
			setOptionsLoading(false);
			setOptionsError('');
			setSelectedOption('');
			setNewLocationName('');
			setCustomItemTypeName('');
			setCustomLocationName('');
			setItemTypeError('');
			setResolvedContext(null);
		}
	}, [open]);

	if (!open || !baseRow) return null;

	const closeModal = () => {
		if (creatingLocation || creatingItemType) return;
		onClose?.();
	};

	const handleUseExisting = () => {
		const key = selectedOption;
		if (!key) return;
		const choice = filteredOptions.find(opt => String(opt.FitmentLocationKey) === key);
		if (!choice) return;
		const resolvedKey = toIntOrNull(choice.FitmentLocationKey) ?? choice.FitmentLocationKey;
		const modelCtx = {
			ModelKey: baseRow.ModelKey,
			Make: baseRow.Make,
			ModelName: baseRow.ModelName,
			StreetName: baseRow.StreetName,
			Year: baseRow.Year,
			Region: baseRow.Region,
			ModelSortOrder: baseRow.ModelSortOrder ?? 0
		};
		const itemCtx = {
			ItemTypeKey: baseRow.ItemTypeKey,
			ItemType: baseRow.ItemType
		};
		onAddGhostRow?.(baseRow, modelCtx, itemCtx, {
			FitmentLocationKey: resolvedKey,
			FitmentLocation: choice.FitmentLocation,
			SortOrder: choice.SortOrder,
			ItemTypeKey: baseRow.ItemTypeKey,
			OneOffGroupKey: choice.OneOffGroupKey ?? null
		});
		if (choice.isOneOff || choice.OneOffGroupKey) {
			onOneOffPromoted?.({
				FitmentLocationKey: resolvedKey,
				ItemTypeKey: baseRow.ItemTypeKey,
				OneOffGroupKey: choice.OneOffGroupKey ?? null,
				SortOrder: choice.SortOrder
			});
		}
		closeModal();
	};

	const handleCreateLocation = async () => {
		const trimmedLocationName = newLocationName.trim();
		if (!trimmedLocationName) {
			alert('Enter a name for the new one-off location.');
			return;
		}
		try {
			setCreatingLocation(true);
			const sortValue = computeAlphaSortValue(trimmedLocationName);
			if (!productGroupKey) {
				if (!modelKey) {
					throw new Error('Missing model context for one-off creation.');
				}
				const payload = {
					name: trimmedLocationName,
					itemTypeKey: baseRow.ItemTypeKey
				};
				if (sortValue !== null) payload.sortOrder = sortValue;
				if (effectiveProductGroupKey) payload.productGroupKey = effectiveProductGroupKey;
				if (effectiveSubProductGroupKey) payload.subProductGroupKey = effectiveSubProductGroupKey;
				const { data } = await axios.post(`/api/models/${modelKey}/one-off-locations`, payload);

				const location = {
					FitmentLocationKey: data.FitmentLocationKey ?? data?.location?.FitmentLocationKey,
					FitmentLocation: data.name ?? data?.location?.FitmentLocation ?? trimmedLocationName,
					SortOrder: data.SortOrder ?? data?.location?.SortOrder ?? sortValue ?? null,
					ItemTypeKey: baseRow.ItemTypeKey,
					OneOffGroupKey: data.OneOffGroupKey ?? null,
					isOneOff: true
				};
				const modelCtx = {
					ModelKey: baseRow.ModelKey,
					Make: baseRow.Make,
					ModelName: baseRow.ModelName,
					StreetName: baseRow.StreetName,
					Year: baseRow.Year,
					Region: baseRow.Region,
					ModelSortOrder: baseRow.ModelSortOrder ?? 0
				};
				const itemCtx = {
					ItemTypeKey: baseRow.ItemTypeKey,
					ItemType: baseRow.ItemType
				};
				onAddGhostRow?.(baseRow, modelCtx, itemCtx, location);
				onOneOffPromoted?.({
					FitmentLocationKey: location.FitmentLocationKey,
					ItemTypeKey: baseRow.ItemTypeKey,
					OneOffGroupKey: location.OneOffGroupKey,
					SortOrder: location.SortOrder
				});
				setOptions(prev => [...prev, location]);
				setResolvedContext(data?.context || null);
			} else {
				if (!subProductGroupKey) {
					alert('Select a sub-group before creating a one-off location.');
					setCreatingLocation(false);
					return;
				}
				const payload = {
					name: trimmedLocationName,
					itemTypeKey: baseRow.ItemTypeKey
				};
				if (sortValue !== null) payload.sortOrder = sortValue;
				const { data } = await axios.post(`/api/sub-product-groups/${subProductGroupKey}/one-off-groups`, payload);

				const location = {
					FitmentLocationKey: data.FitmentLocationKey,
					FitmentLocation: data.name,
					SortOrder: data.SortOrder,
					ItemTypeKey: baseRow.ItemTypeKey,
					OneOffGroupKey: data.OneOffGroupKey,
					isOneOff: true
				};
				const modelCtx = {
					ModelKey: baseRow.ModelKey,
					Make: baseRow.Make,
					ModelName: baseRow.ModelName,
					StreetName: baseRow.StreetName,
					Year: baseRow.Year,
					Region: baseRow.Region,
					ModelSortOrder: baseRow.ModelSortOrder ?? 0
				};
				const itemCtx = {
					ItemTypeKey: baseRow.ItemTypeKey,
					ItemType: baseRow.ItemType
				};
				onAddGhostRow?.(baseRow, modelCtx, itemCtx, location);
				onOneOffPromoted?.({
					FitmentLocationKey: location.FitmentLocationKey,
					ItemTypeKey: baseRow.ItemTypeKey,
					OneOffGroupKey: data.OneOffGroupKey,
					SortOrder: location.SortOrder
				});
				setOptions(prev => [...prev, location]);
			}
			setNewLocationName('');
			setCreatingLocation(false);
			closeModal();
		} catch (err) {
			console.error('Failed to create one-off:', err);
			alert(err.response?.data?.error || 'Failed to create one-off location.');
			setCreatingLocation(false);
		}
	};

	const handleCreateItemType = async () => {
		if (!canCreateItemType) {
			setItemTypeError('You do not have permission to create custom item types.');
			return;
		}
		const trimmedItemTypeName = customItemTypeName.trim();
		if (!trimmedItemTypeName) {
			setItemTypeError('Enter a name for the custom Item Type.');
			return;
		}
		const locationName = customLocationName.trim();
		const itemTypeSort = computeAlphaSortValue(trimmedItemTypeName);
		const locationSort = computeAlphaSortValue(locationName || trimmedItemTypeName);

		setItemTypeError('');
		try {
			setCreatingItemType(true);
			if (!productGroupKey) {
				if (!modelKey) {
					throw new Error('Missing model context for custom item type.');
				}
				const payload = {
					name: trimmedItemTypeName,
					FitmentLocationName: locationName
				};
				if (itemTypeSort !== null) payload.SortOrder = itemTypeSort;
				if (locationSort !== null) payload.FitmentLocationSortOrder = locationSort;
				if (effectiveProductGroupKey) payload.ProductGroupKey = effectiveProductGroupKey;
				if (effectiveSubProductGroupKey) payload.SubProductGroupKey = effectiveSubProductGroupKey;
				const { data } = await axios.post(`/api/models/${modelKey}/custom-one-off-item-type`, payload);

				const modelCtx = {
					ModelKey: baseRow.ModelKey,
					Make: baseRow.Make,
					ModelName: baseRow.ModelName,
					StreetName: baseRow.StreetName,
					Year: baseRow.Year,
					Region: baseRow.Region,
					ModelSortOrder: baseRow.ModelSortOrder ?? 0
				};
				const itemCtx = {
					ItemTypeKey: data.ItemTypeKey,
					ItemType: data.ItemTypeName
				};
				const location = {
					FitmentLocationKey: data.FitmentLocationKey,
					FitmentLocation: data.FitmentLocationName ?? locationName,
					SortOrder: data.SortOrderOneOff ?? data.FitmentLocationSortOrder ?? null,
					ItemTypeKey: data.ItemTypeKey,
					OneOffGroupKey: data.OneOffGroupKey ?? null,
					isOneOff: true
				};

				onAddGhostRow?.(baseRow, modelCtx, itemCtx, location);
				onOneOffPromoted?.({
					FitmentLocationKey: location.FitmentLocationKey,
					ItemTypeKey: data.ItemTypeKey,
					OneOffGroupKey: location.OneOffGroupKey,
					SortOrder: location.SortOrder
				});
				setOptions(prev => [...prev, location]);
				setResolvedContext(data?.context || null);
			} else {
				if (!subProductGroupKey) {
					setItemTypeError('Select both Product Group and Sub-Group first.');
					setCreatingItemType(false);
					return;
				}
				const payload = {
					name: trimmedItemTypeName,
					ProductGroupKey: productGroupKey,
					SubProductGroupKey: subProductGroupKey,
					FitmentLocationName: locationName
				};
				if (itemTypeSort !== null) payload.SortOrder = itemTypeSort;
				if (locationSort !== null) payload.FitmentLocationSortOrder = locationSort;
				const { data: newType } = await axios.post('/api/item-types', payload);

				const promoteBody = {
					fitmentLocationKey: newType.FitmentLocationKey,
					itemTypeKey: newType.ItemTypeKey
				};
				const { data: promotion } = await axios.post(`/api/sub-product-groups/${subProductGroupKey}/one-off-groups`, promoteBody);

				const modelCtx = {
					ModelKey: baseRow.ModelKey,
					Make: baseRow.Make,
					ModelName: baseRow.ModelName,
					StreetName: baseRow.StreetName,
					Year: baseRow.Year,
					Region: baseRow.Region,
					ModelSortOrder: baseRow.ModelSortOrder ?? 0
				};
				const itemCtx = {
					ItemTypeKey: newType.ItemTypeKey,
					ItemType: newType.ItemTypeName
				};
				const location = {
					FitmentLocationKey: promotion.FitmentLocationKey,
					FitmentLocation: promotion.name ?? newType.FitmentLocationName ?? '',
					SortOrder: promotion.SortOrder,
					ItemTypeKey: newType.ItemTypeKey,
					OneOffGroupKey: promotion.OneOffGroupKey,
					isOneOff: true
				};

				onAddGhostRow?.(baseRow, modelCtx, itemCtx, location);
				onOneOffPromoted?.({
					FitmentLocationKey: location.FitmentLocationKey,
					ItemTypeKey: newType.ItemTypeKey,
					OneOffGroupKey: promotion.OneOffGroupKey,
					SortOrder: location.SortOrder
				});
				setOptions(prev => [...prev, location]);
			}

			setCustomItemTypeName('');
			setCustomLocationName('');
			setCreatingItemType(false);
			closeModal();
		} catch (err) {
			console.error('Failed to create custom Item Type:', err);
			setItemTypeError(err.response?.data?.error || 'Failed to create custom Item Type.');
			setCreatingItemType(false);
		}
	};

	return (
		<div style={{
			position: 'fixed',
			inset: 0,
			background: 'rgba(0,0,0,0.45)',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 1600
		}}>
			<div style={{
				width: 'min(720px, 90vw)',
				maxHeight: '90vh',
				overflowY: 'auto',
				background: 'var(--input-bg)',
				color: 'var(--text)',
				borderRadius: 12,
				padding: '1.25rem',
				boxShadow: '0 16px 40px rgba(0,0,0,0.35)'
			}}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
					<div>
						<h3 style={{ margin: 0 }}>Add One-Off Row</h3>
						<div style={{ fontSize: 13, color: '#6b7280' }}>{modelLabel || 'Selected model'} · Item Type: {baseRow.ItemType}</div>
					</div>
					<button onClick={closeModal} style={{ border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 20, cursor: 'pointer' }} aria-label="Close add one-off modal">×</button>
				</div>

				<section style={{ border: '1px solid rgba(148,163,184,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
					<h4 style={{ margin: '0 0 8px' }}>Add Fitment Location</h4>
					{optionsLoading ? (
						<div style={{ color: '#6b7280' }}>Loading available fitment locations…</div>
					) : optionsError ? (
						<div style={{ color: '#b91c1c' }}>{optionsError}</div>
					) : filteredOptions.length === 0 ? (
						<div style={{ color: '#6b7280' }}>No fitment locations are available to add for this Item Type.</div>
					) : (
						<>
							<select
								value={selectedOption}
								onChange={e => setSelectedOption(e.target.value)}
								style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border)' }}
							>
								<option value="">— select —</option>
								{filteredOptions.map(opt => {
									const value = String(opt.FitmentLocationKey);
									const parts = [opt.FitmentLocation || 'Unnamed'];
									if (opt.SortOrder != null) parts.push(`(Sort ${opt.SortOrder})`);
									if (opt.isCurrent) parts.push('· Current Row');
									else if (opt.isAlreadyOnModel) parts.push('· Already on model');
									if (opt.isOneOff) parts.push('[One-Off]');
									return (
										<option key={value} value={value}>
											{parts.join(' ')}
										</option>
									);
								})}
							</select>
							<button
								onClick={handleUseExisting}
								disabled={!selectedOption}
								style={{ marginTop: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--primary)', color: 'var(--button-text)', cursor: selectedOption ? 'pointer' : 'not-allowed' }}
							>
								Add Selected Location
							</button>
						</>
					)}
				</section>

				<section style={{ border: '1px solid rgba(148,163,184,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
					<h4 style={{ margin: '0 0 8px' }}>Create New One-Off Location</h4>
					<div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr auto' }}>
						<input
							placeholder="New fitment location name"
							value={newLocationName}
							onChange={e => setNewLocationName(e.target.value)}
							style={{ padding: '8px', borderRadius: 6, border: '1px solid var(--border)' }}
						/>
						<button
							onClick={handleCreateLocation}
							disabled={creatingLocation}
							style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--button-bg)', color: 'var(--button-text)', cursor: creatingLocation ? 'default' : 'pointer' }}
						>
							{creatingLocation ? 'Creating…' : 'Create & Add'}
						</button>
					</div>
					<p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>Creates a custom fitment location for the current Item Type. Sort order is auto-generated from the name.</p>
				</section>

				<section style={{ border: '1px solid rgba(148,163,184,0.3)', borderRadius: 10, padding: '12px 16px' }}>
					<h4 style={{ margin: '0 0 8px' }}>Create Custom Item Type (One-Off Only)</h4>
					<p style={{ margin: '0 0 12px', fontSize: 12, color: '#6b7280' }}>
						Generates a custom Item Type linked only to this sub-group, creates its first fitment location, and promotes it as a one-off row. Sort ranks are auto-generated.
					</p>
					{itemTypeError && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{itemTypeError}</div>}
					<div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
						<input
							placeholder="Item Type name"
							value={customItemTypeName}
							onChange={e => setCustomItemTypeName(e.target.value)}
							style={{ padding: '8px', borderRadius: 6, border: '1px solid var(--border)' }}
						/>
						<input
							placeholder="Fitment location name"
							value={customLocationName}
							onChange={e => setCustomLocationName(e.target.value)}
							style={{ padding: '8px', borderRadius: 6, border: '1px solid var(--border)' }}
						/>
					</div>
					<button
						onClick={handleCreateItemType}
						disabled={creatingItemType || !canCreateItemType}
						style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: '#1f2937', color: '#f9fafb', cursor: creatingItemType ? 'default' : 'pointer' }}
					>
						{creatingItemType ? 'Creating custom Item Type…' : 'Create Item Type & Add Row'}
					</button>
				</section>
			</div>
		</div>
	);
}
